/**
 * <s-product-gallery> — One-image-at-a-time product gallery with arrows and a thumbnail strip.
 *
 * Mobile keeps native swiping (the arrows and thumbnails are hidden there). On PC the shopper
 * moves through the photos with the arrows or by clicking a thumbnail; the active thumbnail is
 * outlined and kept in view.
 *
 * Required child markup:
 *   [data-product-gallery]               — Horizontal scroll-snap container, one slide per media.
 *   [data-product-gallery] > [data-media-id] — The slides.
 *
 * Optional child markup:
 *   [data-gallery-prev] / [data-gallery-next] — Arrow buttons.
 *   [data-gallery-thumbs]                — Thumbnail strip container.
 *   [data-gallery-thumb="<index>"]       — Thumbnail buttons; aria-current marks the active one.
 *
 * Public API:
 *   showMedia(mediaId) — Scrolls to the slide for that media id (used by s-variant-media).
 */
class SProductGallery extends HTMLElement {
  #gallery = null;
  #slides = [];
  #index = 0;
  #frame = 0;
  #abort = null;

  connectedCallback() {
    this.#gallery = this.querySelector('[data-product-gallery]');
    if (!this.#gallery) return;
    this.#slides = [...this.#gallery.querySelectorAll(':scope > [data-media-id]')];
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', event => this.#onClick(event), { signal });
    this.#gallery.addEventListener('scroll', () => this.#onScroll(), { passive: true, signal });
    this.#update(0);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    cancelAnimationFrame(this.#frame);
  }

  showMedia(mediaId) {
    const index = this.#slides.findIndex(slide => slide.dataset.mediaId === String(mediaId));
    if (index >= 0) this.#goTo(index);
  }

  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-gallery-prev]')) return this.#goTo(this.#index - 1);
    if (target.closest('[data-gallery-next]')) return this.#goTo(this.#index + 1);
    const thumb = target.closest('[data-gallery-thumb]');
    if (thumb) this.#goTo(Number(thumb.dataset.galleryThumb));
  }

  #goTo(index) {
    const total = this.#slides.length;
    if (!total) return;
    const next = Math.max(0, Math.min(total - 1, index));
    const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    this.#gallery.scrollTo({ left: this.#slides[next].offsetLeft, behavior });
    this.#update(next);
  }

  #onScroll() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const width = this.#gallery.clientWidth || 1;
      // Scroll direction flips in right-to-left layouts; the absolute position still maps to a slide.
      this.#update(Math.round(Math.abs(this.#gallery.scrollLeft) / width));
    });
  }

  #update(index) {
    const total = this.#slides.length;
    this.#index = Math.max(0, Math.min(total - 1, index));

    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    if (prev) prev.disabled = this.#index <= 0;
    if (next) next.disabled = this.#index >= total - 1;

    for (const thumb of this.querySelectorAll('[data-gallery-thumb]')) {
      const active = Number(thumb.dataset.galleryThumb) === this.#index;
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
      if (active) this.#revealThumb(thumb);
    }
  }

  #revealThumb(thumb) {
    const strip = this.querySelector('[data-gallery-thumbs]');
    if (!strip || strip.scrollWidth <= strip.clientWidth) return;
    const left = thumb.offsetLeft - (strip.clientWidth - thumb.clientWidth) / 2;
    strip.scrollTo({ left, behavior: 'smooth' });
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', SProductGallery);
